import { 
  TrendingUp, 
  Globe, 
  DollarSign, 
  Briefcase, 
  Cpu, 
  Rocket, 
  Users, 
  BarChart 
} from "lucide-react";

export type Category = {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  color: string;
};

export type Article = {
  id: string;
  title: string;
  excerpt: string;
  content?: string;
  author: {
    name: string;
    avatar: string;
  };
  category: string;
  publishedAt: string;
  readTime: number;
  imageUrl: string;
  thumbnailUrl?: string; // Added thumbnail URL property
  featured?: boolean;
  trending?: boolean;
  priority: 'high' | 'normal';
};

export const categories: Category[] = [
  {
    id: "business",
    name: "Business",
    icon: Briefcase,
    color: "bg-blue-100 text-blue-700",
  },
  {
    id: "technology",
    name: "Technology",
    icon: Cpu,
    color: "bg-purple-100 text-purple-700",
  },
  {
    id: "finance",
    name: "Finance",
    icon: DollarSign,
    color: "bg-green-100 text-green-700",
  },
  {
    id: "global",
    name: "Global",
    icon: Globe,
    color: "bg-amber-100 text-amber-700",
  },
  {
    id: "startups",
    name: "Startups",
    icon: Rocket,
    color: "bg-red-100 text-red-700",
  },
  {
    id: "markets",
    name: "Markets",
    icon: TrendingUp,
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    id: "leadership",
    name: "Leadership",
    icon: Users,
    color: "bg-indigo-100 text-indigo-700",
  },
  {
    id: "economy",
    name: "Economy",
    icon: BarChart,
    color: "bg-teal-100 text-teal-700",
  },
];

// Featured achievers for the Achievers page
export const featuredAchievers: Article[] = [
  {
    id: "custom-13",
    title: "From ₹18,000 to ₹20 Crores: How Rohit Built Infinite Interiors from the Ground Up",
    excerpt: "Rohit started Infinite Interiors with just ₹18,000 and a sketchbook. Today, it’s a ₹20 crore interior design firm known for honest work, high-end projects, and quiet resilience.",
    author: {
      name: "Rohit Kumar",
      avatar: "/Images/Rohit.jpg",
    },
    category: "business",
    publishedAt: "2023-10-26T10:00:00Z",
    readTime: 5,
    imageUrl: "/Images/Rohit.jpg",
    thumbnailUrl: "/Images/Rohit.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-16",
    title: "From Repeated Failure to the Face of Clean Water in South India",
    excerpt: "Balakrishna turned ₹1 lakh and a decade of experience into AQUAPOT — a ₹50+ crore clean water movement. From failed exams to water hero, his mission is proof that purpose can power profit.",
    author: {
      name: "B.M. Balakrishna",
      avatar: "/Images/B.M. Balakrishna.png",
    },
    category: "business",
    publishedAt: "2023-10-20T09:30:00Z",
    readTime: 7,
    imageUrl: "/Images/B.M. Balakrishna.png",
    thumbnailUrl: "/Images/B.M. Balakrishna.png",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-15",
    title: "With Passionate Pocket Money, Setbacks Became Comebacks",
    excerpt: "Naveen Reddy turned pocket money and passion into Mr. Irani — a multi-crore tea franchise that’s changing how India experiences chai. His journey shows how resilience can be steeped into every cup of success.",
    author: {
      name: "Kodudula Naveen Reddy",
      avatar: "/Images/Naveen Reddy.png",
    },
    category: "business",
    publishedAt: "2023-10-15T11:00:00Z",
    readTime: 6,
    imageUrl: "/Images/Naveen Reddy.png",
    thumbnailUrl: "/Images/Naveen Reddy.png",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-14",
    title: "From 0 to Lakhs",
    excerpt: "Uday, founder of Uday Arts, turned blank walls into canvases of fame. From hand-painted corners to celebrity murals, he built a thriving brand that now inspires over 100K followers and employs young artists across South India.",
    author: {
      name: "Uday",
      avatar: "/Images/Uday.png",
    },
    category: "business",
    publishedAt: "2023-10-10T15:45:00Z",
    readTime: 6,
    imageUrl: "/Images/Uday.png",
    thumbnailUrl: "/Images/Uday.png",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-13",
    title: "From Bicycle to Brand",
    excerpt: "Purushotham, the founder of Nilambari Silk Store, transformed a sari-laden bicycle into a respected silk retail enterprise. His journey from door-to-door salesman to retail visionary is a tale of trust, tradition, and unshakable belief in cultural heritage.",
    author: {
      name: "Purushotham",
      avatar: "/Images/Purushotham.png",
    },
    category: "business",
    publishedAt: "2023-10-05T15:45:00Z",
    readTime: 6,
    imageUrl: "/Images/Purushotham.png",
    thumbnailUrl: "/Images/Purushotham.png",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-12",
    title: "Tears Turned to 1Cr Turnover: journey of not just Survival but of Scale.",
    excerpt: "Bhanu Priya transformed adversity into opportunity, scaling Amma Selection into a ₹1 crore wholesale clothing business. Her journey from personal struggles to entrepreneurial success showcases the power of resilience in the textile industry.",
    author: {
      name: "Bhanu Priya",
      avatar: "/Images/Amma Selections.jpg",
    },
    category: "business",
    publishedAt: "2023-09-30T15:45:00Z",
    readTime: 6,
    imageUrl: "/Images/Amma Selections.jpg",
    thumbnailUrl: "/Images/Amma Selections.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-11",
    title: "From Tailoring Challenges to Tech Triumph",
    excerpt: "Anasuya Reddy transformed everyday tailoring challenges into an innovative tech solution with Run Tailor App. Her platform brings structure, scale and visibility to local tailors while providing customers with seamless service...",
    author: {
      name: "Anasuya Reddy",
      avatar: "/Images/Anasuya-Reddy.jpg",
    },
    category: "technology",
    publishedAt: "2023-09-25T10:00:00Z",
    readTime: 6,
    imageUrl: "/Images/Anasuya-Reddy.jpg",
    thumbnailUrl: "/Images/Anasuya-Reddy.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-10",
    title: "Age Is Just a Number",
    excerpt: "Narasimha Reddy's journey with Mukunda Jewellers proves that age is never a barrier when vision meets perseverance. Starting later in life, he built a trusted jewellery empire focused on relationships and integrity...",
    author: {
      name: "Narasimha Reddy",
      avatar: "/Images/Narasimha-Reddy.jpg",
    },
    category: "leadership",
    publishedAt: "2023-09-20T09:30:00Z",
    readTime: 7,
    imageUrl: "/Images/Narasimha-Reddy.jpg",
    thumbnailUrl: "/Images/Narasimha-Reddy.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-9",
    title: "From Government Job to Crore Club",
    excerpt: "Pavan Kumar's journey from a secure government job to building a thriving beverage empire shows that success isn't about safety—it's about seizing opportunities. His brands now serve over ₹25 lakhs monthly through innovative systems and authentic experiences...",
    author: {
      name: "Pavan Kumar",
      avatar: "/Images/Pavan-Kumar.jpg",
    },
    category: "business",
    publishedAt: "2023-09-15T11:00:00Z",
    readTime: 6,
    imageUrl: "/Images/Pavan-Kumar.jpg",
    thumbnailUrl: "/Images/Pavan-Kumar.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-8",
    title: "From Software to Spice",
    excerpt: "Sahithi Chiluveru transitioned from tech to tradition, transforming her passion for Telugu pickles into a ₹1 crore business while creating opportunities for women artisans. Her brand Flavours of Telugu combines heritage recipes with modern entrepreneurship.",
    author: {
      name: "Sahithi Chiluveru",
      avatar: "/Images/sahithi chiluveru.jpg",
    },
    category: "business",
    publishedAt: "2023-09-10T15:45:00Z",
    readTime: 6,
    imageUrl: "/Images/sahithi chiluveru.jpg",
    thumbnailUrl: "/Images/sahithi chiluveru.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-7",
    title: "From Struggle to Success",
    excerpt: "Naveen Mudaliar's life changed drastically when he turned his financial struggles into opportunities. Today, he is the proud owner of multiple malls and continues to inspire aspiring entrepreneurs with his unwavering determination...",
    author: {
      name: "Naveen Mudaliar",
      avatar: "/Images/naveen.jpg",
    },
    category: "business",
    publishedAt: "2023-09-05T15:45:00Z",
    readTime: 6,
    imageUrl: "/Images/naveen.jpg",
    thumbnailUrl: "/Images/naveen.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-6",
    title: "From Cart to Crores",
    excerpt: "Brother Satish transformed a simple street cart serving Telugu fish curry into Poruginti Chepala Pulusu, now a ₹3 crore food brand. His journey from humble beginnings to culinary success showcases the power of authenticity and perseverance...",
    author: {
      name: "Satish",
      avatar: "/Images/Satish.jpg",
    },
    category: "business",
    publishedAt: "2023-08-30T15:45:00Z",
    readTime: 6,
    imageUrl: "/Images/Satish.jpg",
    thumbnailUrl: "/Images/Satish.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-5",
    title: "From Struggles to Success",
    excerpt: "Vijaya Ramineni overcame financial and societal hurdles to establish Mona Lisa Academy, now a premier institute for makeup and beauty skills that has trained hundreds of aspiring artists...",
    author: {
      name: "Vijaya Ramineni",
      avatar: "/Images/Vijaya Ramineni.jpg",
    },
    category: "leadership",
    publishedAt: "2023-08-25T10:00:00Z",
    readTime: 6,
    imageUrl: "/Images/Vijaya Ramineni.jpg",
    thumbnailUrl: "/Images/Vijaya Ramineni.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-4",
    title: "Transforming Careers with findHR",
    excerpt: "Sama Praveen Reddy is revolutionizing the job market with findHR, an app connecting job seekers with real-time HR experts. His innovative approach has helped thousands find the right career opportunities...",
    author: {
      name: "Sama Praveen Reddy",
      avatar: "/Images/sama praveen.jpg",
    },
    category: "technology",
    publishedAt: "2023-08-20T09:30:00Z",
    readTime: 7,
    imageUrl: "/Images/sama praveen.jpg",
    thumbnailUrl: "/Images/sama praveen.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-3",
    title: "From ₹500 to ₹5 Crore",
    excerpt: "Ramya Nagendra started her business with just ₹500. Her dedication and sharp business acumen have transformed her venture into a ₹5 crore fashion empire, setting an example for aspiring entrepreneurs...",
    author: {
      name: "Ramya Nagendra",
      avatar: "/Images/ramaya nagendar.jpg",
    },
    category: "business",
    publishedAt: "2023-08-15T11:00:00Z",
    readTime: 6,
    imageUrl: "/Images/ramaya nagendar.jpg",
    thumbnailUrl: "/Images/ramaya nagendar.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-2",
    title: "From Dreamer to Achiever",
    excerpt: "CA Althaf has transformed the lives of over 10,000 students through his dedication to education. As a mentor and author, he continues to inspire aspiring chartered accountants to achieve their dreams...",
    author: {
      name: "CA Althaf",
      avatar: "/Images/CA althaf.jpg",
    },
    category: "finance",
    publishedAt: "2023-08-10T15:45:00Z",
    readTime: 6,
    imageUrl: "/Images/CA althaf.jpg",
    thumbnailUrl: "/Images/CA althaf.jpg",
    featured: true,
    priority: "high"
  },
  {
    id: "custom-1",
    title: "Transforming the Salon Industry",
    excerpt: "Ashraf Ali's journey from a small-town entrepreneur to the founder of Aniq Studio Salon is nothing short of inspiring. With a clear vision and dedication, he redefined the salon industry, creating a premium experience for customers...",
    author: {
      name: "Ashraf Ali",
      avatar: "/Images/ashraf ali.jpg",
    },
    category: "business",
    publishedAt: "2023-08-05T15:45:00Z",
    readTime: 6,
    imageUrl: "/Images/ashraf ali.jpg",
    thumbnailUrl: "/Images/ashraf ali.jpg",
    featured: true,
    priority: "high"
  }
];

// Regular achievers for the Achievers page (now empty as we're removing this section)
export const regularAchievers: Article[] = [];

// Utility functions for accessing article data
export const getFeaturedArticles = () => {
  // Return empty array as we're focusing on achievers
  return [];
};

export const getTrendingArticles = () => {
  // Return empty array as we're focusing on achievers
  return [];
};

export const getRecentArticles = () => {
  // Return empty array as we're focusing on achievers
  return [];
};

export const getHighlyValuedPeople = () => {
  return featuredAchievers;
};

export const getRegularValuedPeople = () => {
  return regularAchievers;
};

export const getArticlesByCategory = (categoryId: string) => {
  return [...featuredAchievers, ...regularAchievers].filter(article => article.category === categoryId);
};

export const getCategoryById = (categoryId: string) => {
  return categories.find(category => category.id === categoryId);
};

export const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};
